// leanExport — minimal JSON-shape för user-facing export.
//
// Strippad version av WorkflowState/MultiSheetWorkflowState som ENDAST
// inkluderar struktur (block, group, sheet, name, color, connection)
// + den korta `description` som syns på block-kortet. Inga icons,
// subjects, mtime, waypoints, labels, metadata, attachments,
// port-schemas. Lång `detailedDescription` (markdown från Detaljer-
// dialogen) är OPT-IN via `includeAll`-flaggan.
//
// Default (`includeAll=false`): basic data utan detailed description —
// snabbt att dela, läcker ingen detaljerad markdown. Checkbox i
// ExportJsonDialog flippar till `includeAll=true` när user vill
// inkludera även detail-text + icon/subject/to.
//
// Re-import:
//   - Saknade fält tolkas som tomma (description = '', icon = undefined).
//   - Lean och full-format detekteras via `format`-markern.
//
// För AI-internal-pipeline → använd `compactWorkflowState` istället.
// För DB-storage / hydrering → se Lager C (entity_details + bucket).

import type {
  WorkflowState,
  MultiSheetWorkflowState,
  WorkflowBlock,
  Connector,
  BlockGroup,
  WorkflowSheet,
  CrossSheetConnector,
  PortSide,
} from '@/types/workflow';
import { isMultiSheetState, wrapSingleSheet } from '@/types/workflow';

export interface LeanBlock {
  id: string;
  type: string;
  x: number;
  y: number;
  name: string;
  color: string;
  /** Kort summering visad på block-kortet. Alltid med när non-empty. */
  description?: string;
  /** Lång markdown från Detaljer-dialogen. Endast när includeAll=true. */
  detailedDescription?: string;
  /** Lucide-ikonnamn. Endast när includeAll=true. */
  icon?: string;
  /** Email-subject (send-email). Endast när includeAll=true. */
  subject?: string;
  /** Email-to (send-email). Endast när includeAll=true. */
  to?: string;
}

export interface LeanConnector {
  id: string;
  from: string;
  to: string;
  fromPort?: PortSide;
  toPort?: PortSide;
  /** Connector-text. Endast när includeAll=true. */
  label?: string;
}

export interface LeanGroup {
  id: string;
  blockIds: string[];
  color: string;
  /** Optional — group-namn endast om det är satt (icke-tom). */
  name?: string;
  /** True = collapsed/layer card, false = expanded frame. */
  collapsed?: boolean;
  /** Parent group id för nesting (undefined = top-level). */
  parent?: string;
}

export interface LeanSheet {
  id: string;
  name: string;
  /** Sheet-tab-color från meta.color, om satt. */
  color?: string;
  blocks: LeanBlock[];
  connectors: LeanConnector[];
  groups: LeanGroup[];
}

export interface LeanCrossConnector {
  id: string;
  fromSheet: number;
  toSheet: number;
  from: string;
  to: string;
  fromPort?: PortSide;
  toPort?: PortSide;
}

export interface LeanWorkflowExport {
  /** Format-marker så import-pathen kan detektera lean-shape. */
  format: 'lean-1';
  name: string;
  sheets: LeanSheet[];
  cross?: LeanCrossConnector[];
  /** Active sheet vid export — kan ignoreras vid import. */
  active?: number;
}

function leanBlock(b: WorkflowBlock, includeAll: boolean): LeanBlock {
  const out: LeanBlock = {
    id: b.id,
    type: b.type,
    x: b.x,
    y: b.y,
    name: b.config?.name ?? '',
    color: b.color,
  };
  // `description` (kort summering) ingår alltid när non-empty — det är
  // kärninformation som syns på block-kortet och spelar ingen roll för
  // "detailed"-toggle:n.
  const desc = b.config?.description;
  if (desc && desc.trim().length > 0) out.description = desc;
  if (includeAll) {
    const detailed = b.config?.detailedDescription;
    if (detailed && detailed.trim().length > 0) out.detailedDescription = detailed;
    if (b.config?.icon) out.icon = b.config.icon;
    if (b.config?.subject) out.subject = b.config.subject;
    if (b.config?.to) out.to = b.config.to;
  }
  return out;
}

function leanConnector(c: Connector, includeAll: boolean): LeanConnector {
  const out: LeanConnector = { id: c.id, from: c.fromId, to: c.toId };
  if (c.fromPort) out.fromPort = c.fromPort;
  if (c.toPort) out.toPort = c.toPort;
  if (includeAll && c.label) out.label = c.label;
  return out;
}

function leanGroup(g: BlockGroup): LeanGroup {
  const out: LeanGroup = { id: g.id, blockIds: g.blockIds, color: g.color };
  if (g.name && g.name.trim().length > 0) out.name = g.name;
  if (g.collapsed) out.collapsed = true;
  if (g.parentGroupId) out.parent = g.parentGroupId;
  return out;
}

function leanSheet(s: WorkflowSheet, includeAll: boolean): LeanSheet {
  const out: LeanSheet = {
    id: s.id,
    name: s.name,
    blocks: s.blocks.map((b) => leanBlock(b, includeAll)),
    connectors: s.connectors.map((c) => leanConnector(c, includeAll)),
    groups: s.groups.map(leanGroup),
  };
  if (s.meta?.color) out.color = s.meta.color;
  return out;
}

function leanCrossConnector(c: CrossSheetConnector): LeanCrossConnector {
  const out: LeanCrossConnector = {
    id: c.id,
    fromSheet: c.fromSheetIndex,
    toSheet: c.toSheetIndex,
    from: c.fromBlockId,
    to: c.toBlockId,
  };
  if (c.fromPort) out.fromPort = c.fromPort;
  if (c.toPort) out.toPort = c.toPort;
  return out;
}

export interface LeanExportOptions {
  /**
   * Inkludera den långa `detailedDescription`, plus icon/subject/to/label.
   * Default `false` → endast strukturell data + kort `description`.
   * Kontrolleras av "Export all"-checkboxen i ExportJsonDialog.
   */
  includeAll?: boolean;
}

/**
 * Producera lean export-format från godtycklig (single- eller multi-
 * sheet) WorkflowState. Auto-wrappar single-sheet till multi.
 *
 * Default (`includeAll=false`) ger basic data — id/type/position/name/
 * color + kort `description`. Saknar detailed markdown och icons. Med
 * `includeAll=true` läggs `detailedDescription`, `icon`, `subject`,
 * `to` och connector-`label` till.
 */
export function toLeanExport(
  state: WorkflowState | MultiSheetWorkflowState,
  opts: LeanExportOptions = {},
): LeanWorkflowExport {
  const includeAll = opts.includeAll === true;
  const multi = isMultiSheetState(state) ? state : wrapSingleSheet(state);
  const out: LeanWorkflowExport = {
    format: 'lean-1',
    name: multi.name,
    sheets: multi.sheets.map((s) => leanSheet(s, includeAll)),
  };
  if (multi.crossSheetConnectors.length > 0) {
    out.cross = multi.crossSheetConnectors.map(leanCrossConnector);
  }
  if (typeof multi.activeSheetIndex === 'number' && multi.activeSheetIndex !== 0) {
    out.active = multi.activeSheetIndex;
  }
  return out;
}

/** Type-guard så import-flödet kan plocka rätt parser. */
export function isLeanExport(input: unknown): input is LeanWorkflowExport {
  return (
    input != null &&
    typeof input === 'object' &&
    (input as LeanWorkflowExport).format === 'lean-1' &&
    Array.isArray((input as LeanWorkflowExport).sheets)
  );
}

/**
 * Hydrera lean-export till MultiSheetWorkflowState. Strippade fält
 * fylls med tomma defaults: description='', icon=undefined, label
 * undefined etc. Type-defaultar till 'general' om saknas.
 */
export function fromLeanExport(lean: LeanWorkflowExport): MultiSheetWorkflowState {
  return {
    name: lean.name ?? '',
    activeSheetIndex: typeof lean.active === 'number' ? lean.active : 0,
    sheets: lean.sheets.map((s) => ({
      id: s.id,
      name: s.name,
      blocks: s.blocks.map((b) => ({
        id: b.id,
        type: (b.type ?? 'general') as WorkflowBlock['type'],
        x: b.x,
        y: b.y,
        config: {
          name: b.name ?? '',
          ...(b.description ? { description: b.description } : {}),
          ...(b.detailedDescription ? { detailedDescription: b.detailedDescription } : {}),
          ...(b.icon ? { icon: b.icon } : {}),
          ...(b.subject ? { subject: b.subject } : {}),
          ...(b.to ? { to: b.to } : {}),
        },
        color: b.color ?? '#6366f1',
      })),
      connectors: s.connectors.map((c) => ({
        id: c.id,
        fromId: c.from,
        toId: c.to,
        ...(c.fromPort ? { fromPort: c.fromPort } : {}),
        ...(c.toPort ? { toPort: c.toPort } : {}),
        ...(c.label ? { label: c.label } : {}),
      })),
      groups: s.groups.map((g) => ({
        id: g.id,
        blockIds: g.blockIds ?? [],
        color: g.color ?? '#8b5cf6',
        ...(g.name ? { name: g.name } : {}),
        ...(g.collapsed ? { collapsed: true } : {}),
        ...(g.parent ? { parentGroupId: g.parent } : {}),
      })),
      ...(s.color ? { meta: { color: s.color } } : {}),
    })),
    crossSheetConnectors: (lean.cross ?? []).map((c) => ({
      id: c.id,
      fromSheetIndex: c.fromSheet,
      toSheetIndex: c.toSheet,
      fromBlockId: c.from,
      toBlockId: c.to,
      ...(c.fromPort ? { fromPort: c.fromPort } : {}),
      ...(c.toPort ? { toPort: c.toPort } : {}),
    })),
  };
}
